import { useEffect } from "react";

import { DateTime } from "luxon";

import useMetricsQuery from "@app/analytics/hooks/metrics/useMetricsQuery";
import {
  getQueryDateRangeFromDateTimes,
  ParsedQueryResultRequiredBy,
  parseMetricsDataFromQueryResult,
} from "@app/analytics/utils";
import useLocalStorage from "@app/common/hooks/useLocalStorage";
import { User } from "@app/common/types";
import { EnterpriseTeam } from "@app/common/workspaces/types";

const ALL_TIME_START_DATE = DateTime.fromMillis(0);

type ParsedMeetingCountQueryResult =
  ParsedQueryResultRequiredBy<"meetingCount">;

const ESTABLISHED_MEETINGS_THRESHOLD = 5;

const DEFAULT_RESULT: ParsedMeetingCountQueryResult = {
  meetingCount: { value: null, formattedValue: "-" },
};

const useHasMeetings = ({
  userId,
  teamId,
  enabled = true,
}: {
  userId?: User["id"];
  teamId?: EnterpriseTeam["id"];
  enabled?: boolean;
}) => {
  const [hasEstablishedMeetings, setHasEstablishedMeetings] = useLocalStorage(
    "hasEstablishedMeetings",
    false
  );

  const meetingCountQuery = useMetricsQuery(
    {
      dateRanges: getQueryDateRangeFromDateTimes({
        startDate: ALL_TIME_START_DATE,
        endDate: DateTime.now(),
      }),
      metrics: ["meetingCount"],
      dimensions: [],
      filters: [],
      teamId,
      userId,
      people: userId ? "only_me" : "everyone",
    },
    {
      queryKey: ["meetingCount", userId, teamId],
      enabled: enabled && !hasEstablishedMeetings,
      refetchOnMount: false,
      refetchOnWindowFocus: false,
      staleTime: 2 * 60 * 1000,
    }
  );

  const results =
    parseMetricsDataFromQueryResult<ParsedMeetingCountQueryResult>(
      meetingCountQuery.data,
      DEFAULT_RESULT
    );

  const meetingCount = results[0]?.meetingCount.value ?? 0;

  useEffect(() => {
    if (hasEstablishedMeetings || meetingCount < ESTABLISHED_MEETINGS_THRESHOLD)
      return;
    setHasEstablishedMeetings(true);
  }, [meetingCount, hasEstablishedMeetings]);

  return {
    // User has at least one meeting ever
    hasMeetings: hasEstablishedMeetings || meetingCount > 0,
    // User has more than a threshold of meetings
    hasEstablishedMeetings,
    isLoading: meetingCountQuery.isLoading,
  };
};

export default useHasMeetings;
